// src/components/layout/Sidebar.jsx
import React, { useContext, useState } from 'react';
import {
    Drawer, List, Divider, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Box, useTheme, useMediaQuery,
    Typography, IconButton, Backdrop
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Assessment as AssessmentIcon,
    History as HistoryIcon,
    Settings as SettingsIcon,
    Help as HelpIcon,
    Security as SecurityIcon,
    AdminPanelSettings as AdminIcon,
    RateReview as ReviewIcon,
    Menu as MenuIcon,
    Close as CloseIcon,
    Analytics as AnalyticsIcon,
    BarChart as MetricsIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

// Sidebar width
const drawerWidth = 240;

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
    const { currentUser } = useContext(AuthContext);
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // If not logged in or on auth pages, don't show sidebar
    if (!currentUser || location.pathname === '/login' || location.pathname === '/register') {
        return null;
    }

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLinkClick = () => {
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    // Define sidebar menu items
    const menuItems = [
        {
            text: 'Dashboard',
            icon: <DashboardIcon />,
            path: '/dashboard'
        },
        {
            text: 'New Assessment',
            icon: <AssessmentIcon />,
            path: '/new-assessment'
        },
        {
            text: 'History',
            icon: <HistoryIcon />,
            path: '/history'
        },
        {
            text: 'Analytics',
            icon: <AnalyticsIcon />,
            path: '/analytics'
        },
        {
            text: 'Help',
            icon: <HelpIcon />,
            path: '/help'
        }
    ];

    // Admin-only menu items
    const adminItems = [
        {
            text: 'Reviews Management',
            icon: <ReviewIcon />,
            path: '/admin/reviews'
        },
        {
            text: 'User Management',
            icon: <AdminIcon />,
            path: '/admin/users'
        },
        {
            text: 'Metrics Management',
            icon: <MetricsIcon />,
            path: '/admin/metrics'
        }
    ];

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Mobile header with close button */}
            {isMobile && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SecurityIcon
                            sx={{
                                fontSize: 32,
                                color: 'primary.main',
                                mr: 1
                            }}
                        />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 'bold',
                                color: 'primary.main',
                                letterSpacing: '0.5px'
                            }}
                        >
                            RiskAS
                        </Typography>
                    </Box>
                    <IconButton onClick={handleDrawerToggle} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>
            )}

            {/* Desktop logo section */}
            {!isMobile && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 2,
                            mt: 2,
                            mb: 1
                        }}
                    >
                        <SecurityIcon
                            sx={{
                                fontSize: 40,
                                color: 'primary.main',
                                mr: 1
                            }}
                        />
                        <Typography
                            variant="h4"
                            component={Link}
                            to="/dashboard"
                            onClick={handleLinkClick}
                            sx={{
                                fontWeight: 'bold',
                                color: 'primary.main',
                                textDecoration: 'none',
                                letterSpacing: '0.5px'
                            }}
                        >
                            RiskAS
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                </>
            )}

            <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
                {/* Main Menu Items */}
                <List>
                    {menuItems.map((item) => (
                        <ListItem key={item.text} disablePadding>
                            <ListItemButton
                                component={Link}
                                to={item.path}
                                onClick={handleLinkClick}
                                selected={location.pathname === item.path}
                                sx={{
                                    borderRadius: '0 20px 20px 0',
                                    mx: 1,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.light',
                                        '&:hover': {
                                            backgroundColor: 'primary.light',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>

                {/* Admin Section */}
                {currentUser?.is_admin && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ px: 2, mb: 1 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Administration
                            </Typography>
                        </Box>
                        <List>
                            {adminItems.map((item) => (
                                <ListItem key={item.text} disablePadding>
                                    <ListItemButton
                                        component={Link}
                                        to={item.path}
                                        onClick={handleLinkClick}
                                        selected={location.pathname === item.path}
                                        sx={{
                                            borderRadius: '0 20px 20px 0',
                                            mx: 1,
                                            '&.Mui-selected': {
                                                backgroundColor: 'secondary.light',
                                                '&:hover': {
                                                    backgroundColor: 'secondary.light',
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'secondary.main' }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.text}
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    color: location.pathname === item.path ? 'secondary.dark' : 'text.primary'
                                                }
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
            </Box>

            <Divider />

            {/* User info section at bottom */}
            <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    Logged in as:
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                    {currentUser?.full_name || currentUser?.email}
                </Typography>
                {currentUser?.is_admin && (
                    <Typography variant="caption" color="secondary.main" sx={{ display: 'block', fontWeight: 'bold' }}>
                        Administrator
                    </Typography>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            {/* Mobile Drawer */}
            {isMobile ? (
                <>
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: drawerWidth,
                            },
                        }}
                    >
                        {drawerContent}
                    </Drawer>
                </>
            ) : (
                /* Desktop Permanent Drawer */
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

// Export a mobile toggle component for the header
export const MobileMenuButton = ({ mobileOpen, setMobileOpen }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (!isMobile) return null;

    return (
        <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2 }}
        >
            <MenuIcon />
        </IconButton>
    );
};

export default Sidebar;